import { maxAttachmentCount } from "api/constants";
import { EAttachmentType } from "api/enums";
import { IRowMovement } from "api/interfaces";
import AttachmentModel from "api/models/AttachmentModel";
import ContractRowModel from "api/models/ContractRowModel";
import AttachmentService from "api/services/AttachmentService";
import ContractRowMovimentService from "api/services/ContractRowMovimentService";
import { lang } from "i18nLang";
import { makeAutoObservable } from "mobx";
import { ContractStore, MessageStore } from "..";

class Store {
  public movimentsAttachments: {
    attachments: AttachmentModel[];
    attachmentsToExclude: AttachmentModel[];
  }[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  public reset() {
    this.movimentsAttachments = [];
  }

  public async fetchMovimentsAttachments(moviments: IRowMovement[]) {
    this.reset();

    const contract = ContractStore.getCurrentContract();
    if (!contract) return;

    for (const moviment of moviments) {
      const attachments = await AttachmentService.getAttachmentsByIds(
        contract.uid,
        moviment.attachments || []
      );
      this.addMovimentAttachments(attachments);
    }
  }

  public hasFileBeingUploaded(index: number) {
    const attachments = this.getMovimentAttachments(index);
    return attachments.some((att) => att.isUploading());
  }

  public validadeAttachmentsCount(index: number, files: File[]) {
    const attachments = this.getMovimentAttachments(index);
    return files.length + attachments.length <= maxAttachmentCount;
  }

  public uploadMovimentAttachment(index: number, file: File) {
    return new Promise(async (resolve) => {
      try {
        const contractId = ContractStore.getCurrentContract()?.uid;
        if (!contractId) return;

        const attachments = this.getMovimentAttachments(index);
        await AttachmentService.validateAttachment(file);

        const attachment = AttachmentService.uploadAttachmentToStorage(
          contractId,
          file,
          EAttachmentType.MOVIMENTS,
          () => {
            MessageStore.error(lang("attachments.errors.uploadError"));
          },
          () => {
            resolve(true);
          }
        );
        this.addAttachmentToList(attachments, attachment);
      } catch (error: any) {
        MessageStore.error(error.message);
        resolve(false);
      }
    });
  }

  public async removeAllUnsavedMovimentAttachments() {
    const promises: Promise<void>[] = [];
    let index = 0;
    for (const moviment of this.movimentsAttachments) {
      for (const attachment of moviment.attachments) {
        if (!attachment.isSaved()) {
          promises.push(this.removeMovimentAttachment(index, attachment));
        }
      }
      index++;
    }
    await Promise.all(promises);
  }

  public async removeMovimentAttachment(
    index: number,
    attachment: AttachmentModel
  ) {
    const contractId = ContractStore.getCurrentContract()?.uid;
    if (!contractId) return;

    if (attachment.isSaved()) {
      this.removeAttachmentFromList(index, attachment);
      const attachmentsToExclude = this.getMovimentAttachmentsToExclude(index);
      attachmentsToExclude.push(attachment);
      return;
    }

    if (attachment.isUploading()) {
      attachment.cancelUpload();
      this.removeAttachmentFromList(index, attachment);
      return;
    }

    return AttachmentService.removeAttachmentFromStorage(contractId, attachment)
      .then(() => {
        this.removeAttachmentFromList(index, attachment);
      })
      .catch(() => {
        MessageStore.error(lang("attachments.errors.removeError"));
      });
  }

  public async saveMovimentAttachments(
    index: number,
    rows: ContractRowModel[]
  ): Promise<void> {
    let contract = ContractStore.getCurrentContract();
    if (!contract || !rows?.length) return;

    const attachments = this.getMovimentAttachments(index).filter((att) =>
      att.isSuccessfullyUploaded()
    );
    const attachmentsToExclude = this.getMovimentAttachmentsToExclude(index);

    MessageStore.setLoading(true);
    await AttachmentService.applyAttachments(
      contract,
      attachments,
      attachmentsToExclude,
      async (attUids, transaction) => {
        const promises = rows.map((row) =>
          ContractRowMovimentService.updateMovimentAttachments(
            row,
            index,
            attUids,
            transaction
          )
        );
        await Promise.all(promises);
      }
    ).catch(() => {
      MessageStore.error(lang("attachments.errors.saveError"));
    });

    this.clearAttachmentsToExclude(index);
    MessageStore.setLoading(false);
  }

  public getMovimentAttachments(index: number) {
    return this.movimentsAttachments[index]?.attachments || [];
  }

  public getMovimentAttachmentsToExclude(index: number) {
    return this.movimentsAttachments[index]?.attachmentsToExclude || [];
  }

  public addMovimentAttachments(attachments: AttachmentModel[]) {
    this.movimentsAttachments.push(
      makeAutoObservable({
        attachments,
        attachmentsToExclude: [],
      })
    );
  }

  private addAttachmentToList(
    attachments: AttachmentModel[],
    attachment: AttachmentModel
  ) {
    attachments.push(makeAutoObservable(attachment));
  }

  private removeAttachmentFromList(index: number, attachment: AttachmentModel) {
    const attachments = this.getMovimentAttachments(index);
    const indexAtt = attachments.indexOf(attachment);
    if (indexAtt !== -1) {
      attachments.splice(indexAtt, 1);
    }
  }

  private clearAttachmentsToExclude(index: number) {
    this.movimentsAttachments[index].attachmentsToExclude = [];
  }
}

const MovimentsAttachmentStore = new Store();
export default MovimentsAttachmentStore;
